import { useCallback, useRef, useState } from "react";
import { BarChart3, Download } from "lucide-react";
import type { Reading } from "@spf/contracts";
import { api } from "../lib/client.js";
import { useAsync, type Async } from "../lib/useAsync.js";
import { useLiveTelemetry } from "../lib/useLiveTelemetry.js";
import { useFreshness } from "../lib/useFreshness.js";
import { useBattery, useEnergy, type EnergyResult } from "../lib/useEnergy.js";
import {
  LEDGER_QUERIES,
  LEDGER_TOTALS,
  TODAY_QUERY,
  ledgerOf,
  type LedgerPeriod,
} from "../lib/energy.js";
import { counters } from "../lib/counters.js";
import { Card } from "../components/Card.js";
import { Freshness } from "../components/Freshness.js";
import { Highlights } from "../components/analytics/Highlights.js";
import { Counters } from "../components/analytics/Counters.js";
import { LedgerTable, type Ledger } from "../components/analytics/LedgerTable.js";
import { CostCard } from "../components/analytics/CostCard.js";
import { Empty, Loading } from "../components/ViewState.js";
import { cn } from "../lib/cn.js";

const PERIODS = [
  { id: "month", label: "Month" },
  { id: "ytd", label: "Year to date" },
  { id: "lifetime", label: "Lifetime" },
] as const satisfies readonly { id: LedgerPeriod; label: string }[];

type PeriodId = (typeof PERIODS)[number]["id"];

/**
 * Energy and cost, in the design's analytics layout: the banner with its
 * period switch and export control, four headline cards, the inverter's own
 * counters, what the period cost, then the ledger.
 *
 * Two kinds of figure sit on this page and it keeps them apart. The headline
 * cards and the ledger are energy integrated on the server from stored
 * readings, over the site's own days and months, with any gap declared. The
 * counter cards are registers the inverter keeps itself; they are shown as
 * the inverter reports them and checked against the first kind, never mixed
 * into it.
 *
 * Nothing is computed here. Every figure arrives from the API, so this page
 * and any other client cannot disagree (docs/REQUIREMENTS.md section 3).
 */
export function AnalyticsPage() {
  const { latest } = useLiveTelemetry();
  const { state: site } = useAsync(api.site, "Could not load site details.");
  const heading = useRef<HTMLHeadingElement>(null);
  const freshness = useFreshness(latest);
  const [period, setPeriod] = useState<PeriodId>("ytd");

  const currency = site.kind === "ready" ? site.value.currency : "—";
  const reading = latest?.reading ?? null;

  return (
    <section aria-labelledby="analytics-heading" className="flex flex-col gap-5">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            <BarChart3 size={14} aria-hidden="true" className="text-accent-2-ink" />
            Energy and cost
          </p>
          <h2
            id="analytics-heading"
            ref={heading}
            tabIndex={-1}
            className="text-3xl font-bold tracking-tight text-ink outline-none sm:text-4xl"
          >
            Analytics
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <PeriodSwitch period={period} onChange={setPeriod} />
          <ExportButton reading={reading} />
        </div>
      </div>

      {latest !== null && reading !== null && <Freshness value={latest} freshness={freshness} />}

      {latest === null ? (
        <Card title="Energy">
          <Loading label="Connecting for live updates…" />
        </Card>
      ) : reading === null ? (
        <Card title="Energy">
          <Empty>
            No telemetry yet. Energy figures and the inverter&rsquo;s counters appear here once it
            reports.
          </Empty>
        </Card>
      ) : (
        <Figures reading={reading} period={period} currency={currency} />
      )}
    </section>
  );
}

/**
 * Everything below the banner once there is telemetry. The energy queries live
 * here rather than in the page, so a new installation with nothing recorded
 * does not ask for history it cannot have.
 */
function Figures({
  reading,
  period,
  currency,
}: {
  reading: Reading;
  period: PeriodId;
  currency: string;
}) {
  const today = useEnergy(TODAY_QUERY, reading.time);
  const lifetime = useEnergy(LEDGER_QUERIES.lifetime, reading.time);
  const ledger = useEnergy(LEDGER_QUERIES[period], reading.time);
  const battery = useBattery(LEDGER_QUERIES[period], reading.time);
  const { retry: retryToday } = today;
  const { retry: retryLifetime } = lifetime;
  const { retry: retryLedger } = ledger;
  const { retry: retryBattery } = battery;
  const retryHighlights = useCallback(() => {
    retryToday();
    retryLifetime();
  }, [retryToday, retryLifetime]);
  const retryCost = useCallback(() => {
    retryLedger();
    retryBattery();
  }, [retryLedger, retryBattery]);

  return (
    <>
      <Highlights today={today.state} lifetime={lifetime.state} onRetry={retryHighlights} />
      <Counters reading={reading} lifetime={lifetime.state} onRetry={retryLifetime} />
      <CostCard
        period={LEDGER_TOTALS[period]}
        energy={ledger.state}
        battery={battery.state}
        currency={currency}
        onRetry={retryCost}
      />
      <LedgerTable
        ledger={ledgerState(ledger, period)}
        onRetry={ledger.retry}
        currency={currency}
      />
      <Explainer />
    </>
  );
}

function ledgerState(ledger: EnergyResult, period: PeriodId): Async<Ledger> {
  return ledger.state.kind === "ready"
    ? { kind: "ready", value: ledgerOf(ledger.state.value, period) }
    : ledger.state;
}

/** The design's three-way period switch, choosing what the ledger covers. */
function PeriodSwitch({
  period,
  onChange,
}: {
  period: PeriodId;
  onChange: (period: PeriodId) => void;
}) {
  return (
    <fieldset className="flex items-center rounded-xl border border-line bg-line/40 p-1 text-xs font-medium">
      <legend className="sr-only">Period</legend>
      {PERIODS.map((entry) => (
        <label
          key={entry.id}
          className={cn(
            // The radio itself is visually hidden, so its focus ring would draw
            // on a clipped pixel; the label carries it instead.
            "cursor-pointer rounded-lg px-3 py-1.5 transition-colors",
            "has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-accent-ink",
            period === entry.id
              ? "bg-btn font-semibold text-btn-ink"
              : "text-ink-muted hover:text-ink",
          )}
        >
          <input
            type="radio"
            name="period"
            value={entry.id}
            checked={period === entry.id}
            onChange={() => {
              onChange(entry.id);
            }}
            className="sr-only"
          />
          {entry.label}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * The design's "Export Report", which sets a label for three seconds and
 * produces no file. This one produces the file: the inverter's counters as
 * they stand, under the names that say what each one counts.
 */
function ExportButton({ reading }: { reading: Reading | null }) {
  if (reading === null) return null;

  const download = () => {
    const rows: [string, string][] = [
      ["Reading time", reading.time],
      ...(["today", "total"] as const).flatMap((span) =>
        counters(reading, span).map((counter): [string, string] => [
          `${counter.label}, ${span === "today" ? "today" : "since installation"} (kWh)`,
          String(counter.value ?? ""),
        ]),
      ),
    ];
    const csv = ["Measure,Value", ...rows.map(([name, value]) => `"${name}","${value}"`)].join(
      "\n",
    );
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "inverter-counters.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      onClick={download}
      className="flex items-center gap-1.5 rounded-xl bg-btn px-3.5 py-2 text-xs font-semibold text-btn-ink"
    >
      <Download size={14} aria-hidden="true" />
      Export counters
    </button>
  );
}

function Explainer() {
  return (
    <Card title="What these are">
      <div className="flex max-w-prose flex-col gap-3 text-sm leading-relaxed text-ink-muted">
        <p>
          The headline figures and the ledger are worked out on the server from the stored readings,
          over the site&rsquo;s own days and months. Where readings are missing, the figure covers
          only what was recorded and says how much that was, rather than guessing at the rest.
        </p>
        <p>
          The counter cards are the inverter&rsquo;s own registers, named for what they were
          measured to count: charging from the grid, discharge from the battery, the part of the
          house&rsquo;s load that the grid supplied, and what the panels generated.
          &ldquo;Today&rdquo; resets at midnight on the inverter&rsquo;s own clock, which is set by
          hand.
        </p>
        <p>
          Cost is priced on the server, each quarter hour at the tariff in force when it started.
          The battery&rsquo;s energy is followed from where it was charged to where it went, so a
          kWh the grid put in costs more by the time it comes back out: what the round trip lost was
          paid for too.
        </p>
      </div>
    </Card>
  );
}
